import { JSX, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isStateFieldRequired, getCountrySubdivisions } from '@campai/lib-common/utils/address'
import { Address } from '@campai/lib-common/entities'

import { ValidationErrors } from '@/utils/misc'

import { AddressComplete } from './AddressComplete'
import { AddressFields, AddressFieldsBaseProps } from './AddressFields'

type AddressInputChangeEventHandler = (value: Address) => void

type AddressFormContainerProps = {
    label?: string
    info?: ReactNode
    required?: boolean
    disabled?: boolean
    readOnly?: boolean
    className?: string
}

type AddressInputProps = AddressFormContainerProps &
    AddressFieldsBaseProps & {
        name: string
        autoFocus?: boolean
        errors?: ValidationErrors
        value: Address
        onChange: AddressInputChangeEventHandler
        autoComplete: boolean
        autoCompleteOff?: boolean
    }

export const AddressForm = (props: AddressInputProps): JSX.Element => {
    const { autoComplete, errors, value, onChange, restrictCountries, preferredCountry, name } = props

    const hasAddress = value.addressLine || value.zip || value.city

    const { i18n } = useTranslation()

    const [showAutoComplete, setShowAutoComplete] = useState(autoComplete)
    const [showManualAddress, setShowManualAddress] = useState(false)
    const [showStateField, setShowStateField] = useState(false)
    const [states, setStates] = useState<Array<{ value: string; label: string }>>([])

    const addressLineRef = useRef<HTMLInputElement | null>(null)
    const addressLineInputId = `${name}-address-line`

    const focusAddressLineInput = () => {
        const input = document.getElementById(addressLineInputId)
        addressLineRef.current = input instanceof HTMLInputElement ? input : null
        return addressLineRef.current
    }

    const hasErrors = useMemo(() => errors && Object.keys(errors).length > 0, [errors])

    useEffect(() => {
        setShowAutoComplete(autoComplete)
    }, [autoComplete])

    useEffect(() => {
        if (value.country) {
            if (isStateFieldRequired(value.country)) {
                setShowStateField(true)

                const queriedStates = getCountrySubdivisions(value.country, { lang: i18n.language })
                setStates(queriedStates)
            } else {
                setShowStateField(false)
                setStates([])
            }
        } else {
            setShowStateField(false)
            setStates([])
        }
    }, [i18n.language, value])

    if (!hasAddress && !showManualAddress && !hasErrors && showAutoComplete) {
        return (
            <AddressComplete
                {...props}
                restrictCountries={restrictCountries}
                preferredCountry={preferredCountry}
                onSelect={(address) => {
                    //
                    // We add a space after the addressLine and focus the input
                    // at the end so user comforts by entering the street number directly
                    //
                    // NOTE: this is actually country dependant so ie in usa it works differently
                    // and we might honor this in future depending on address country
                    //

                    onChange({
                        ...address,
                        addressLine: /\d$/.test(address.addressLine) ? address.addressLine : `${address.addressLine} `,
                    })

                    setTimeout(() => {
                        const input = focusAddressLineInput()
                        if (input) {
                            input.focus()
                            input.setSelectionRange(input.value.length, input.value.length)
                        }
                    }, 0)
                }}
                onManualAddress={() => {
                    setShowManualAddress(true)
                    setTimeout(() => {
                        const input = focusAddressLineInput()
                        if (input) {
                            input.focus()
                        }
                    }, 0)
                }}
            />
        )
    }

    return (
        <AddressFields
            {...props}
            addressLineInputId={addressLineInputId}
            state={showStateField}
            errors={errors}
            states={states}
            value={value}
            onChange={onChange}
        />
    )
}
